import type { OrderDetailResponse } from '@fitting-room/contracts'
import { MAX_INVOICE_RECIPIENTS } from '@fitting-room/contracts/order-math'

/**
 * Who an invoice is emailed to, worked out before anybody types: the
 * addresses the send panel starts with and the ones it offers. Kept out of
 * the page so it can be tested in `node` like the rest of `lib/`.
 * `docs/22-back-office.md` § "Invoice".
 */

/** Loose on purpose: the API has the real rule, this only catches the obvious typo. */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** One address however it was capitalized or spaced, as the API counts them. */
const keyOf = (address: string) => address.trim().toLowerCase()

export interface InvoiceRecipients {
  /** What the To fields start with. Never empty: one blank field to type in when there is nobody to start with. */
  readonly prefill: readonly string[]
  /**
   * Other addresses on file — from the appointments linked to the order —
   * offered one click away and never added on their own: a mother's address
   * on the booking is somebody the customer may not want the invoice sent to.
   */
  readonly suggestions: readonly string[]
}

/**
 * The order's own email first. Without one, the first linked appointment
 * that has an address stands in for it — the appointments come soonest
 * first, so that is the one the order was most likely written at — and the
 * rest are suggestions. An appointment's address the order already has, in
 * any case, is not offered twice.
 */
export function invoiceRecipients(detail: Pick<OrderDetailResponse, 'order' | 'appointments'>): InvoiceRecipients {
  const onFile: string[] = []
  const seen = new Set<string>()
  const add = (address: string | undefined) => {
    const trimmed = address?.trim()
    if (!trimmed || seen.has(keyOf(trimmed))) return
    seen.add(keyOf(trimmed))
    onFile.push(trimmed)
  }
  add(detail.order.customerEmail)
  for (const visit of detail.appointments) add(visit.customerEmail)
  const [first, ...rest] = onFile
  return { prefill: [first ?? ''], suggestions: rest }
}

/**
 * The chips under the To fields: every address on file — the one the fields
 * started with as well as the others — that isn't in a field now. So a
 * prefilled address cleared or mistyped is offered back, and an order whose
 * email changed while the page was open offers the new one. A chip goes
 * once its address is in a field, clicked or typed.
 */
export function openSuggestions(start: InvoiceRecipients, recipients: readonly string[]): string[] {
  const taken = new Set(recipients.map(keyOf).filter(Boolean))
  const offered = new Set<string>()
  const out: string[] = []
  for (const address of [...start.prefill, ...start.suggestions]) {
    const key = keyOf(address)
    if (!key || taken.has(key) || offered.has(key)) continue
    offered.add(key)
    out.push(address.trim())
  }
  return out
}

/**
 * Whether the list can take no more: as many fields as an invoice goes to,
 * none of them blank. A blank field is room — a chip fills it.
 */
export function recipientsFull(recipients: readonly string[]): boolean {
  return recipients.length >= MAX_INVOICE_RECIPIENTS && recipients.every((r) => r.trim() !== '')
}

/**
 * A suggestion clicked: into the first blank field if there is one, else a
 * new field — unless that would be one more than an invoice goes to, when
 * nothing changes. `index` is the field it went into (`null`: none), so the
 * page can put the cursor there.
 */
export function addRecipient(recipients: readonly string[], address: string): { recipients: string[]; index: number | null } {
  const blank = recipients.findIndex((r) => !r.trim())
  if (blank >= 0) return { recipients: recipients.map((r, i) => (i === blank ? address : r)), index: blank }
  if (recipients.length >= MAX_INVOICE_RECIPIENTS) return { recipients: [...recipients], index: null }
  return { recipients: [...recipients, address], index: recipients.length }
}

export interface RecipientCheck {
  /** The addresses to send to: trimmed, blanks dropped, one copy each. */
  readonly to: string[]
  /** One per field, `null` where the field is fine (a blank one is). */
  readonly fields: (string | null)[]
  /** About the list as a whole. */
  readonly problem: string | null
}

export function checkRecipients(recipients: readonly string[]): RecipientCheck {
  const seen = new Set<string>()
  const fields = recipients.map((raw) => {
    const value = raw.trim()
    if (!value) return null
    if (!EMAIL.test(value)) return "That doesn't look like an email address."
    if (seen.has(keyOf(value))) return 'That address is already on the list.'
    seen.add(keyOf(value))
    return null
  })
  const to: string[] = []
  const kept = new Set<string>()
  for (const raw of recipients) {
    const value = raw.trim()
    if (!value || kept.has(keyOf(value))) continue
    kept.add(keyOf(value))
    to.push(value)
  }
  const problem =
    to.length === 0
      ? 'Add at least one address.'
      : to.length > MAX_INVOICE_RECIPIENTS
        ? `An invoice goes to at most ${MAX_INVOICE_RECIPIENTS} addresses.`
        : null
  return { to, fields, problem }
}

/**
 * Whether the panel may offer to send, from `GET /api/admin/invoice-email`
 * and what the last send said.
 *
 * Only a status that has ARRIVED and says `configured: true` lets Send be
 * pressed. While it loads the answer is "not yet", not "yes": the dialog
 * this page replaced read a missing answer as configured and had Send live
 * for the moment before it knew. A status request that failed is not
 * sendable either — nobody knows if a send would go anywhere — and a send
 * answered `configured: false` (the server's mailer changed under the page)
 * counts as the server having said no.
 */
export type SendReadiness = 'checking' | 'ready' | 'not_configured' | 'unknown'

export function sendReadiness(status: {
  readonly isError: boolean
  readonly configured: boolean | undefined
  readonly answeredNotConfigured: boolean
}): SendReadiness {
  if (status.answeredNotConfigured) return 'not_configured'
  if (status.configured === true) return 'ready'
  if (status.configured === false) return 'not_configured'
  if (status.isError) return 'unknown'
  return 'checking'
}

/** Whether the list is ready to send: an address, none malformed or repeated, not too many. */
export function recipientsReady(check: RecipientCheck): boolean {
  return check.problem === null && check.fields.every((field) => field === null)
}
